import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useWebSocket } from '../../contexts/WebSocketContext';
import AgentCard, {
  AgentStatusKind,
  AgentWarning,
} from './AgentCard';
import AgentDrawer from './AgentDrawer';
import type {
  AgentSession,
  GrowthAgentConfig,
  KnowledgeAgentScope,
  WebSocketEvent,
} from '../../types';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface AgentsPanelProps {
  scope: KnowledgeAgentScope;
  scopeId: number;
  scopeName?: string;
}

// `growth_agent` = the autonomous taxonomy + page-build LLM loop (the
// thing we renamed from "Discovery"). `growth_session` = a row from
// ProjectAgentSession — historically the same agent's per-session
// activity log, shown in the "Page builder activity" stream below.
// (The knowledge agent moved to the settings → knowledge page; see
// KnowledgeSuggestLauncher.)
type StreamAgent = 'growth_agent' | 'growth_session';

interface StreamItem {
  id: string;
  timestamp: string;
  agent: StreamAgent;
  agentLabel: string;
  description: string;
}

interface ProjectHealth {
  total_categories: number;
  total_topics: number;
  total_keywords: number;
  total_unique_keywords: number;
  cross_topic_duplicates: number;
  pinned_keywords: number;
  settled_keywords: number;
  topics: Array<{
    topic_id: number;
    topic_name: string;
    category_name: string;
    keyword_count: number;
    pinned_count: number;
    duplicate_keywords: string[];
  }>;
}

const STREAM_PAGE_SIZE = 20;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function agentSessionDescription(s: AgentSession): string {
  if (s.status === 'running') return 'Running page-builder agent…';
  if (s.status === 'failed') return 'Page-builder run failed';
  const parts: string[] = [];
  if (s.pages_built) parts.push(`${s.pages_built} pages built`);
  if (s.recs_accepted) parts.push(`${s.recs_accepted} recs accepted`);
  if (s.recs_rejected) parts.push(`${s.recs_rejected} recs rejected`);
  if (s.pages_promoted) parts.push(`${s.pages_promoted} promoted`);
  if (s.pages_refined) parts.push(`${s.pages_refined} refined`);
  return parts.length ? parts.join(', ') : 'Completed';
}

// A completed session with zero output across every counter is noise — hide it.
function isMeaningfulGrowthSession(s: AgentSession): boolean {
  if (s.status === 'running' || s.status === 'failed') return true;
  return !!(
    s.pages_built ||
    s.recs_accepted ||
    s.recs_rejected ||
    s.pages_promoted ||
    s.pages_refined
  );
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

export default function AgentsPanel({ scope, scopeId, scopeName }: AgentsPanelProps) {
  const { subscribe } = useWebSocket();

  // ---- Growth-agent state (project scope only) ----
  const [growthAgentConfig, setGrowthAgentConfig] = useState<GrowthAgentConfig | null>(null);
  // Set when a growth_agent_llm `started` event arrives, cleared on
  // session_complete / failed. There's no DB-backed "is a run in
  // progress" record any more (DiscoveryRun went away with the old
  // LangGraph pipeline) so we derive it from the WS stream.
  const [isGrowthAgentRunning, setIsGrowthAgentRunning] = useState(false);
  const [growthAgentLive, setGrowthAgentLive] = useState<string>('');
  const [projectHealth, setProjectHealth] = useState<ProjectHealth | null>(null);

  // ---- Growth agent sessions (project scope only) ----
  const [growthSessions, setGrowthSessions] = useState<AgentSession[]>([]);

  // ---- Stream pagination ----
  const [streamCount, setStreamCount] = useState(STREAM_PAGE_SIZE);

  // ---- Drawer state ----
  const [drawerAgent, setDrawerAgent] = useState<'growth_agent' | null>(null);

  // -- Fetch sources --
  const refreshGrowthAgent = useCallback(async () => {
    if (scope !== 'project') return;
    try {
      const [cfg, health] = await Promise.all([
        api.getGrowthAgentConfig(scopeId).catch(() => null),
        api.getProjectHealth(scopeId).catch(() => null),
      ]);
      if (cfg) setGrowthAgentConfig(cfg);
      if (health) setProjectHealth(health);
    } catch {
      /* best-effort */
    }
  }, [scope, scopeId]);

  const refreshGrowth = useCallback(async () => {
    if (scope !== 'project') return;
    try {
      const sessions = await api.getAgentSessions(scopeId, 20, 0);
      setGrowthSessions(sessions);
    } catch {
      /* best-effort */
    }
  }, [scope, scopeId]);

  useEffect(() => {
    refreshGrowthAgent();
    refreshGrowth();
  }, [refreshGrowthAgent, refreshGrowth]);

  // -- Live events --
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      // Growth agent — project scope only. The autonomous loop emits
      // these from `run_agent_session`; we drive both the card's
      // running indicator and the page-builder activity refresh from
      // here since session rows land in ProjectAgentSession on completion.
      if (event.event_type === 'growth_agent_llm' && scope === 'project') {
        if (event.project_id !== scopeId) return;
        if (event.message) setGrowthAgentLive(event.message);
        if (event.status === 'started') {
          setIsGrowthAgentRunning(true);
        } else if (
          event.status === 'session_complete' ||
          event.status === 'failed'
        ) {
          setIsGrowthAgentRunning(false);
          refreshGrowthAgent();
          refreshGrowth();
        }
      }
    });
  }, [
    subscribe,
    scope,
    scopeId,
    refreshGrowthAgent,
    refreshGrowth,
  ]);

  // -- Derived: agent-card descriptors --

  const growthAgentCard = useMemo(() => {
    if (scope !== 'project') return null;

    const isEnabled = !!growthAgentConfig?.is_enabled;

    let statusKind: AgentStatusKind = 'never_run';
    let statusLabel = 'Never run';
    if (isGrowthAgentRunning) {
      statusKind = 'running';
      statusLabel = 'Running';
    } else if (isEnabled) {
      statusKind = 'enabled';
      statusLabel = 'Enabled';
    }

    const infoLine = growthAgentLive || (isEnabled ? 'Waiting for next cycle' : 'Disabled');

    const warnings: AgentWarning[] = [];
    if (projectHealth) {
      if (projectHealth.cross_topic_duplicates > 0) {
        warnings.push({
          text: `${projectHealth.cross_topic_duplicates} cross-topic duplicate${
            projectHealth.cross_topic_duplicates === 1 ? '' : 's'
          }`,
          severity: 'error',
        });
      }
    }

    return { statusKind, statusLabel, infoLine, warnings, isRunning: isGrowthAgentRunning, isEnabled };
  }, [scope, growthAgentConfig, isGrowthAgentRunning, growthAgentLive, projectHealth]);

  // -- Actions --

  const handleGrowthAgentRunNow = async () => {
    try {
      await api.triggerGrowthAgentRun(scopeId);
      setGrowthAgentLive('Run triggered, starting within 30 seconds…');
    } catch (e: any) {
      alert(e?.message ?? 'Failed to trigger run');
    }
  };

  // -- Stream: merge all sources into one sorted list --

  const streamItems = useMemo((): StreamItem[] => {
    const items: StreamItem[] = [];

    if (scope === 'project') {
      growthSessions.forEach((s) => {
        if (!isMeaningfulGrowthSession(s)) return;
        items.push({
          id: `growth-session-${s.id}`,
          timestamp: s.created_at,
          agent: 'growth_session',
          agentLabel: 'Page builder',
          description: agentSessionDescription(s),
        });
      });
    }

    items.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
    return items;
  }, [scope, growthSessions]);

  // Per-agent recent activity (shown inline under each agent card)
  const RECENT_PER_AGENT = 3;
  const recentByAgent = useMemo(() => {
    const by: Record<StreamAgent, { id: string; timeLabel: string; description: string }[]> = {
      growth_agent: [],
      growth_session: [],
    };
    for (const item of streamItems) {
      const bucket = by[item.agent];
      if (bucket.length < RECENT_PER_AGENT) {
        bucket.push({
          id: item.id,
          timeLabel: relTime(item.timestamp),
          description: item.description,
        });
      }
    }
    return by;
  }, [streamItems]);

  // Residual activity: page-builder session rows shown under the agent
  // cards (the rest are surfaced inline under each card).
  const residualStream = useMemo(
    () => streamItems.filter((i) => i.agent === 'growth_session'),
    [streamItems]
  );
  const visibleStream = residualStream.slice(0, streamCount);
  const hasMoreStream = residualStream.length > streamCount;

  // -- Drawer content --

  const drawerContent = () => {
    if (drawerAgent === 'growth_agent' && scope === 'project') {
      return (
        <div className="space-y-4 text-sm">
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">Current status</div>
            <div className="text-gray-600 dark:text-gray-300 mt-1">
              {growthAgentCard?.infoLine || '-'}
            </div>
          </div>
          {projectHealth && (
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">Subject health</div>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[
                  { label: 'Topics', value: projectHealth.total_topics },
                  { label: 'Cats', value: projectHealth.total_categories },
                  { label: 'KWs', value: projectHealth.total_keywords },
                  {
                    label: 'Dupes',
                    value: projectHealth.cross_topic_duplicates,
                    color:
                      projectHealth.cross_topic_duplicates > 0
                        ? 'text-red-600 dark:text-red-400'
                        : undefined,
                  },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-center"
                  >
                    <div
                      className={`text-sm font-semibold ${
                        stat.color || 'text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {stat.value}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Pinned: {projectHealth.pinned_keywords} · Settled:{' '}
                {projectHealth.settled_keywords}
              </div>
              {projectHealth.topics.length > 0 && (
                <div className="mt-3 max-h-60 overflow-y-auto border-t border-gray-100 dark:border-gray-700 pt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 dark:text-gray-400 border-b">
                        <th className="py-1 pr-2 font-medium">Topic</th>
                        <th className="py-1 pr-2 font-medium">Category</th>
                        <th className="py-1 pr-2 font-medium text-right">KWs</th>
                        <th className="py-1 pr-2 font-medium text-right">Pins</th>
                        <th className="py-1 font-medium">Dupes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectHealth.topics.map((t) => (
                        <tr
                          key={t.topic_id}
                          className="border-b border-gray-100 dark:border-gray-700"
                        >
                          <td
                            className="py-1 pr-2 truncate max-w-[140px]"
                            title={t.topic_name}
                          >
                            {t.topic_name}
                          </td>
                          <td
                            className="py-1 pr-2 text-gray-500 dark:text-gray-400 truncate max-w-[100px]"
                            title={t.category_name}
                          >
                            {t.category_name}
                          </td>
                          <td className="py-1 pr-2 text-right">
                            {t.keyword_count}
                          </td>
                          <td className="py-1 pr-2 text-right">
                            {t.pinned_count}
                          </td>
                          <td
                            className={`py-1 truncate max-w-[120px] ${
                              t.duplicate_keywords.length > 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-400 dark:text-gray-500'
                            }`}
                            title={t.duplicate_keywords.join(', ')}
                          >
                            {t.duplicate_keywords.length > 0
                              ? t.duplicate_keywords.join(', ')
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const drawerTitle = drawerAgent === 'growth_agent' ? 'Growth agent' : '';

  // -- Render --

  const showResidualStream = residualStream.length > 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-3 space-y-2">
      <h2 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider">
        Agents
      </h2>

      {/* Agent cards — flex-wrap so they stack side-by-side when room allows */}
      <div className="flex flex-wrap gap-3">
        {scope === 'project' && growthAgentCard && (
          <AgentCard
            name="Growth agent"
            scopeBadge="project"
            status={{ kind: growthAgentCard.statusKind, label: growthAgentCard.statusLabel }}
            infoLine={growthAgentCard.infoLine}
            warnings={growthAgentCard.warnings}
            primaryAction={{
              label: 'Run Now',
              onClick: handleGrowthAgentRunNow,
              disabled: growthAgentCard.isRunning,
            }}
            menuActions={[
              { label: 'Settings', to: `/projects/${scopeId}/settings/growth-agent` },
            ]}
            recentActivity={recentByAgent.growth_agent}
            onOpenDrawer={() => setDrawerAgent('growth_agent')}
          />
        )}
      </div>

      {/* Residual activity — only agents without a card (e.g. page builder) */}
      {showResidualStream && (
        <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
          <div className="text-xs font-medium uppercase text-gray-400 dark:text-gray-500 tracking-wider mb-1">
            Page builder activity
          </div>
          <ul className="max-h-48 overflow-y-auto">
            {visibleStream.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 py-1 text-sm"
              >
                <span className="text-xs text-gray-400 dark:text-gray-500 w-16 flex-shrink-0">
                  {relTime(item.timestamp)}
                </span>
                <span className="text-gray-700 dark:text-gray-300 truncate">{item.description}</span>
              </li>
            ))}
          </ul>
          {hasMoreStream && (
            <div className="px-1 py-2 flex items-center justify-between">
              <span className="text-xs text-gray-400 dark:text-gray-500">
                Showing {visibleStream.length} of {residualStream.length}
              </span>
              <button
                onClick={() => setStreamCount(streamCount + STREAM_PAGE_SIZE)}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                Show more
              </button>
            </div>
          )}
        </div>
      )}

      <AgentDrawer
        open={drawerAgent !== null}
        onClose={() => setDrawerAgent(null)}
        title={drawerTitle}
        subtitle={scopeName}
        size="default"
      >
        {drawerContent()}
      </AgentDrawer>
    </div>
  );
}
